/**
 * AdsPreviewPage — free lead-magnet landing page.
 * Users enter URL + target keyword, see 1 Google Ads variant immediately, and
 * must enter an email to unlock the remaining 2 variants. The email is
 * captured into ads_preview_leads for marketing follow-up.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { ToolHeader } from "./CompetitorSpyPage";
import { Sparkles, Loader2, Lock, Mail, Zap, ExternalLink } from "lucide-react";

const inputCls = "w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none";

export default function AdsPreviewPage() {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({ url: "", keyword: "", email: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const generate = async (e) => {
    e?.preventDefault();
    if (!form.url || !form.keyword) return;
    setLoading(true);
    const r = await axios.post(`${API}/growth/ads-preview`, {
      url: form.url, keyword: form.keyword, language: language || "en",
    });
    setResult(r.data);
    setLoading(false);
  };

  const unlock = async () => {
    if (!form.email.includes("@")) return;
    setUnlocking(true);
    const r = await axios.post(`${API}/growth/ads-preview`, {
      url: form.url, keyword: form.keyword, email: form.email, language: language || "en",
    });
    setResult(r.data);
    setUnlocking(false);
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <ToolHeader />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 bg-[#a855f7]/10 border border-[#a855f7]/30 text-[#c084fc] text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
            <Sparkles className="w-3 h-3" /> {t.ads_pv_eyebrow || "FREE AI Preview"}
          </div>
          <h1 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tight mb-3" data-testid="ads-pv-title">
            {t.ads_pv_title || "See your Google Ads — before you spend a cent."}
          </h1>
          <p className="text-lg text-[var(--color-text-muted)] max-w-2xl mx-auto">
            {t.ads_pv_sub || "Paste your URL + target keyword → AI writes 3 high-CTR ad variants in 10 seconds. No sign-up for the first one."}
          </p>
        </div>

        <form onSubmit={generate} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-5 grid md:grid-cols-3 gap-3 mb-8" data-testid="ads-pv-form">
          <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="https://yoursite.com" className={inputCls} data-testid="ads-pv-url" />
          <input required value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })}
            placeholder={t.ads_pv_kw || "Target keyword (e.g. AI SEO tool)"} className={inputCls} data-testid="ads-pv-kw" />
          <button type="submit" disabled={loading || !form.url || !form.keyword} data-testid="ads-pv-generate"
            className="px-5 py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {t.ads_pv_cta || "Generate free"}
          </button>
        </form>

        {result && (
          <div className="space-y-4" data-testid="ads-pv-results">
            {result.variants.map((v, i) => (
              <AdMockup key={i} v={v} index={i} testid={`ads-pv-variant-${i}`} />
            ))}

            {result.locked_count > 0 && (
              <div className="bg-[var(--color-surface)] border-2 border-dashed border-[#a855f7]/40 p-8 text-center" data-testid="ads-pv-gate">
                <Lock className="w-10 h-10 text-[#a855f7] mx-auto mb-3" />
                <h3 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-2">
                  {(t.ads_pv_lock_title || "Unlock {n} more variants").replace("{n}", result.locked_count)}
                </h3>
                <p className="text-sm text-[var(--color-text-muted)] mb-5">
                  {t.ads_pv_lock_sub || "Enter your email — we'll send a PDF version you can forward to your marketing team."}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@company.com" className={inputCls + " pl-10"} data-testid="ads-pv-email" />
                  </div>
                  <button onClick={unlock} disabled={unlocking || !form.email.includes("@")} data-testid="ads-pv-unlock"
                    className="px-5 py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2">
                    {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {t.ads_pv_unlock_btn || "Unlock now"}
                  </button>
                </div>
              </div>
            )}

            {!result.gated && (
              <div className="bg-gradient-to-r from-[#a855f7]/10 to-[#67e8f9]/10 border border-[#a855f7]/30 p-6 text-center" data-testid="ads-pv-upsell">
                <p className="text-sm uppercase tracking-wider font-bold text-[#c084fc] mb-1 font-mono">{t.ads_pv_next || "What's next?"}</p>
                <h3 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-2">
                  {t.ads_pv_upsell_title || "Build these ads visually — in PulseStudio."}
                </h3>
                <p className="text-sm text-[var(--color-text-muted)] mb-4">
                  {t.ads_pv_upsell_sub || "Drag-and-drop editor, 100+ templates, one-click export to Meta/Google."}
                </p>
                <a href="/ads-creator" data-testid="ads-pv-upsell-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600">
                  {t.ads_pv_upsell_cta || "Open PulseStudio"} · €69/mo <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Renders a single ad mockup in the classic Google SERP style. */
function AdMockup({ v, index, testid }) {
  return (
    <div className="bg-white text-black p-5 rounded-md max-w-2xl mx-auto shadow-lg" data-testid={testid}>
      <div className="flex items-center gap-1 text-[11px] text-gray-500 mb-1">
        <span className="font-bold text-gray-900">Ad</span> · {v.display_url}
        <span className="ml-auto inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[9px] font-bold uppercase rounded">{v.angle}</span>
      </div>
      <h2 className="text-[18px] leading-snug text-[#1a0dab] hover:underline cursor-pointer font-normal">
        {v.headline1} | {v.headline2} | {v.headline3}
      </h2>
      <p className="text-[13px] text-[#4d5156] leading-snug mt-1">{v.description1}</p>
      <p className="text-[13px] text-[#4d5156] leading-snug">{v.description2}</p>
      <div className="text-[10px] text-gray-400 mt-2 font-mono">Variant #{index + 1} · Generated by TopRankPulse</div>
    </div>
  );
}
